/**
 * Attach a group to the camera without parenting it to the camera.
 *
 * React Three Fiber does not add its default camera to the scene graph, so an
 * object made a child of the camera is simply never rendered. Instead the group
 * stays a normal scene child and copies the camera's world transform every
 * frame; children then position themselves in view space exactly as if they had
 * been parented.
 *
 * Ordering matters: this must run after the camera rig has posed the camera for
 * the frame. `useFrame` callbacks run in registration order, and the rig is
 * mounted above every consumer of this hook.
 */
import { useFrame, useThree } from '@react-three/fiber';
import type { RefObject } from 'react';
import type * as THREE from 'three';

export function useCameraSpace(ref: RefObject<THREE.Group | null>) {
  const { camera } = useThree();
  useFrame(() => {
    const group = ref.current;
    if (!group) return;
    group.position.copy(camera.position);
    group.quaternion.copy(camera.quaternion);
    group.updateMatrixWorld();
  });
}
